import { doc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  Opportunity,
  Requirement,
  ProposalMilestone,
  ProposalAddendum,
  RequiredForm,
  FormatRule,
  ProposalSectionGroup,
  PlanReadinessCheck,
  PlanReadinessReport,
  ReadinessSeverity,
  StageHistoryEntry,
} from "../types";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

export interface AuditPlanReadinessParams {
  opportunity: Opportunity;
  requirements: Requirement[];
  milestones: ProposalMilestone[];
  addenda: ProposalAddendum[];
  forms: RequiredForm[];
  formatRules: FormatRule[];
  sectionGroups: ProposalSectionGroup[];
}

/**
 * Deterministically audits proposal readiness across requirements, sections, addenda, forms, and schedule.
 */
export function auditPlanReadiness(params: AuditPlanReadinessParams): PlanReadinessReport {
  const { opportunity, requirements, milestones, addenda, forms, sectionGroups } = params;

  const checks: PlanReadinessCheck[] = [];

  // Check 1: Mandatory Requirements
  const mandatoryReqs = requirements.filter((r) => r.mandatory);
  const confirmedMandatory = mandatoryReqs.filter((r) => r.confirmed);
  let reqStatus: ReadinessSeverity;
  let reqSummary: string;
  const reqDetails: string[] = [];

  if (requirements.length === 0) {
    reqStatus = "blocker";
    reqSummary = "No requirements extracted or manually added to the matrix.";
    reqDetails.push("Add requirements from RFP documents or create manual items.");
  } else if (mandatoryReqs.length === 0) {
    reqStatus = "pass";
    reqSummary = `All ${requirements.length} requirement(s) are non-mandatory / informational.`;
  } else if (confirmedMandatory.length === mandatoryReqs.length) {
    reqStatus = "pass";
    reqSummary = `All ${mandatoryReqs.length} mandatory requirement(s) confirmed.`;
  } else {
    const unconfirmed = mandatoryReqs.filter((r) => !r.confirmed);
    const ratio = confirmedMandatory.length / mandatoryReqs.length;
    reqStatus = ratio >= 0.7 ? "warning" : "blocker";
    reqSummary = `${unconfirmed.length} of ${mandatoryReqs.length} mandatory requirement(s) not confirmed.`;
    unconfirmed.slice(0, 5).forEach((r) => {
      reqDetails.push(`${r.sectionRef ? r.sectionRef + " " : ""}${r.title}`);
    });
    if (unconfirmed.length > 5) {
      reqDetails.push(`...and ${unconfirmed.length - 5} more.`);
    }
  }

  const reqScore =
    reqStatus === "pass"
      ? 25
      : reqStatus === "warning"
        ? 15
        : Math.round(25 * (confirmedMandatory.length / Math.max(1, mandatoryReqs.length)));

  checks.push({
    id: "mandatory_requirements",
    label: "Mandatory Requirements Verification",
    category: "requirements",
    status: reqStatus,
    score: reqScore,
    summary: reqSummary,
    details: reqDetails,
    actionLabel: "Open Requirements Matrix",
    actionSubTab: "requirements",
  });

  // Check 2: Section Ownership & Lead Drafters
  let secStatus: ReadinessSeverity;
  let secSummary: string;
  const secDetails: string[] = [];

  if (sectionGroups.length === 0) {
    secStatus = "warning";
    secSummary = "No proposal work packages or sections created yet.";
    secDetails.push("Auto-initialize proposal outline from requirements in Section Ownership.");
  } else {
    const unassigned = sectionGroups.filter((s) => !s.assignedTo || !s.assignedTo.trim());
    if (unassigned.length === 0) {
      secStatus = "pass";
      secSummary = `All ${sectionGroups.length} proposal section(s) have assigned lead drafters.`;
    } else if (unassigned.length <= 2 && sectionGroups.length > 2) {
      secStatus = "warning";
      secSummary = `${unassigned.length} section(s) lack a lead drafter.`;
      unassigned.forEach((s) => {
        secDetails.push(`${s.sectionRef} ${s.title}`);
      });
    } else {
      secStatus = "blocker";
      secSummary = `${unassigned.length} of ${sectionGroups.length} sections are unassigned.`;
      unassigned.slice(0, 5).forEach((s) => {
        secDetails.push(`${s.sectionRef} ${s.title}`);
      });
      if (unassigned.length > 5) {
        secDetails.push(`...and ${unassigned.length - 5} more.`);
      }
    }
  }

  const secScore = secStatus === "pass" ? 25 : secStatus === "warning" ? 15 : 0;

  checks.push({
    id: "section_ownership",
    label: "Bid Section Ownership & Drafter Allocation",
    category: "sections",
    status: secStatus,
    score: secScore,
    summary: secSummary,
    details: secDetails,
    actionLabel: "Assign Section Leads",
    actionSubTab: "sections",
  });

  // Check 3: Addenda Compliance & Acknowledgments
  let addendaStatus: ReadinessSeverity;
  let addendaSummary: string;
  const addendaDetails: string[] = [];

  if (addenda.length === 0) {
    addendaStatus = "pass";
    addendaSummary = "No amendments or addenda reported by client.";
  } else {
    const pendingAck = addenda.filter(
      (a) => a.acknowledgmentRequired && a.acknowledgmentStatus !== "acknowledged",
    );
    if (pendingAck.length === 0) {
      addendaStatus = "pass";
      addendaSummary = `All ${addenda.length} addenda acknowledged with signed forms verified.`;
    } else {
      addendaStatus = "blocker";
      addendaSummary = `${pendingAck.length} addenda pending signed acknowledgment forms.`;
      pendingAck.forEach((a) => {
        addendaDetails.push(`${a.number}: ${a.title}`);
      });
    }
  }

  const addendaScore = addendaStatus === "pass" ? 20 : 0;

  checks.push({
    id: "addenda_compliance",
    label: "RFP Addenda & Scope Acknowledgment",
    category: "addenda",
    status: addendaStatus,
    score: addendaScore,
    summary: addendaSummary,
    details: addendaDetails,
    actionLabel: "View Addenda Log",
    actionSubTab: "addenda",
  });

  // Check 4: Required Forms & Attachments
  const mandatoryForms = forms.filter((f) => f.mandatory);
  const pendingForms = mandatoryForms.filter(
    (f) => f.status !== "attached" && f.status !== "signed",
  );
  let formStatus: ReadinessSeverity;
  let formSummary: string;
  const formDetails: string[] = [];

  if (mandatoryForms.length === 0) {
    formStatus = "pass";
    formSummary = "No mandatory compliance forms specified.";
  } else if (pendingForms.length === 0) {
    formStatus = "pass";
    formSummary = `All ${mandatoryForms.length} mandatory form(s) accounted for and attached/signed.`;
  } else {
    formStatus = "warning";
    formSummary = `${pendingForms.length} mandatory form(s) not yet marked as attached or signed.`;
    pendingForms.forEach((f) => {
      formDetails.push(`${f.formName} (Status: ${f.status.replace("_", " ")})`);
    });
  }

  const formScore = formStatus === "pass" ? 15 : 10;

  checks.push({
    id: "forms_compliance",
    label: "Mandatory Compliance Forms",
    category: "forms",
    status: formStatus,
    score: formScore,
    summary: formSummary,
    details: formDetails,
    actionLabel: "Review Required Forms",
    actionSubTab: "forms",
  });

  // Check 5: Response Schedule & Submission Deadline
  let schedStatus: ReadinessSeverity;
  let schedSummary: string;
  const schedDetails: string[] = [];

  if (!opportunity.dueDate || !opportunity.dueDate.trim()) {
    schedStatus = "blocker";
    schedSummary = "No submission due date set on opportunity.";
    schedDetails.push("Specify client submission deadline in workspace header or schedule.");
  } else {
    const todayStr = new Date().toISOString().split("T")[0] || "";
    if (opportunity.dueDate < todayStr) {
      schedStatus = "blocker";
      schedSummary = `Submission due date (${opportunity.dueDate}) has already passed!`;
    } else if (milestones.length === 0) {
      schedStatus = "warning";
      schedSummary = `Due date set for ${opportunity.dueDate}, but no internal gate milestones generated.`;
      schedDetails.push("Generate response milestones from presets in Response Schedule.");
    } else {
      schedStatus = "pass";
      schedSummary = `Submission deadline confirmed (${opportunity.dueDate}) with ${milestones.length} milestone(s) active.`;
    }
  }

  const schedScore = schedStatus === "pass" ? 15 : schedStatus === "warning" ? 8 : 0;

  checks.push({
    id: "schedule_integrity",
    label: "Response Schedule & Gate Milestones",
    category: "schedule",
    status: schedStatus,
    score: schedScore,
    summary: schedSummary,
    details: schedDetails,
    actionLabel: "Manage Schedule",
    actionSubTab: "schedule",
  });

  // Aggregate
  const overallScore = Math.min(
    100,
    Math.max(0, reqScore + secScore + addendaScore + formScore + schedScore),
  );
  const blockersCount = checks.filter((c) => c.status === "blocker").length;
  const warningsCount = checks.filter((c) => c.status === "warning").length;
  const passedCount = checks.filter((c) => c.status === "pass").length;

  const overallStatus: ReadinessSeverity =
    blockersCount > 0 ? "blocker" : warningsCount > 0 ? "warning" : "pass";

  return {
    overallScore,
    overallStatus,
    canFreeze: blockersCount === 0,
    blockersCount,
    warningsCount,
    passedCount,
    checks,
    evaluatedAt: new Date().toISOString(),
  };
}

/**
 * Freezes the proposal plan baseline, advances the stage to 'drafting', and logs audit history.
 */
export async function freezeOpportunityPlan(
  opportunityId: string,
  user: { displayName: string; email: string },
  currentHistory: StageHistoryEntry[] = [],
  readinessScore?: number,
): Promise<void> {
  const now = new Date().toISOString();

  const historyEntry: StageHistoryEntry = {
    stage: "drafting",
    changedAt: now,
    changedBy: user.displayName,
    changedByEmail: user.email,
    note: `Proposal Plan Baseline frozen by ${user.displayName}${
      typeof readinessScore === "number" ? ` (Readiness Score: ${readinessScore}%)` : ""
    }. Stage advanced to Drafting.`,
  };

  const updatedHistory = [...currentHistory, historyEntry];

  const docRef = doc(db, "opportunities", opportunityId);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      planFrozen: true,
      planFrozenAt: now,
      planFrozenBy: user.displayName,
      planFrozenByEmail: user.email,
      planReadinessScore: readinessScore,
      stage: "drafting",
      stageHistory: updatedHistory,
      updatedAt: now,
    }),
  );
}

/**
 * Unfreezes the proposal plan baseline for authorized editing and logs audit history.
 */
export async function unfreezeOpportunityPlan(
  opportunityId: string,
  user: { displayName: string; email: string },
  currentHistory: StageHistoryEntry[] = [],
  reason?: string,
): Promise<void> {
  const now = new Date().toISOString();

  const historyEntry: StageHistoryEntry = {
    stage: "drafting",
    changedAt: now,
    changedBy: user.displayName,
    changedByEmail: user.email,
    note: `Proposal Plan Baseline unfrozen by ${user.displayName}: ${reason || "Reopened for plan updates"}`,
  };

  const updatedHistory = [...currentHistory, historyEntry];

  const docRef = doc(db, "opportunities", opportunityId);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      planFrozen: false,
      stageHistory: updatedHistory,
      updatedAt: now,
    }),
  );
}
